import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { DocumentEditor } from "@/components/documents/DocumentEditor";
import { DocumentSharing } from "@/components/documents/DocumentSharing";
import { DocumentCollaboration } from "@/components/documents/DocumentCollaboration";
import { DocumentDetailEditor } from "@/components/documents/DocumentDetailEditor";
import { generatePDF } from "@/utils/pdf-generator";
import { 
  ArrowLeft, 
  Download, 
  Share2, 
  MessageCircle,
  FileText,
  Users
} from "lucide-react";
import { CSOLoadingSpinner } from "@/components/ui/cso-loading-spinner";

const DocumentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [document, setDocument] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [userSubscription, setUserSubscription] = useState<any>(null);

  useEffect(() => {
    if (id) {
      loadDocument();
      checkUserSubscription();
    }
  }, [id]);

  const checkUserSubscription = async () => {
    try {
      console.log('Checking user subscription...');
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('No user found');
        return;
      }

      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .single();

      if (error) {
        console.error('Error fetching subscription:', error);
      }

      console.log('User subscription data:', data);
      setUserSubscription(data);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const loadDocument = async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('documents')
        .select(`
          *,
          organizations (
            id,
            name
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error loading document:', error);
        toast({
          title: "Error",
          description: "Failed to load document",
          variant: "destructive",
        });
        navigate('/documents');
        return;
      }

      setDocument(data);
    } catch (error: any) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!document) return;
    
    setIsDownloading(true);
    try {
      console.log('Starting PDF download for document:', document.id);
      await generatePDF(document);
      
      toast({
        title: "Download Started",
        description: "Your document is being downloaded as PDF.",
      });
    } catch (error: any) {
      console.error('Error downloading document:', error);
      toast({
        title: "Download Failed",
        description: error.message || "Failed to download document. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CSOLoadingSpinner size="lg" />
      </div>
    );
  }

  if (!document) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Document Not Found</h2>
          <p className="text-gray-600 mb-4">The document you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/documents')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Documents
          </Button>
        </div>
      </div>
    );
  }

  // Only team and enterprise users get collaboration features (not monthly users)
  const isTeamOrEnterprise = userSubscription && (
    userSubscription.subscription_type === 'team' || 
    userSubscription.subscription_type === 'enterprise'
  );

  console.log('Subscription check result:', {
    userSubscription,
    isTeamOrEnterprise,
    subscriptionType: userSubscription?.subscription_type,
    status: userSubscription?.status
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/documents')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{document.title}</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="secondary">{document.type}</Badge>
                {document.organizations && (
                  <Badge variant="outline">
                    <Users className="h-3 w-3 mr-1" />
                    {document.organizations.name}
                  </Badge>
                )}
                {/* Debug badge to show subscription status */}
                {userSubscription && (
                  <Badge variant="outline" className="bg-blue-50">
                    {userSubscription.subscription_type || 'unknown'} ({userSubscription.status})
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={handleDownload}
              disabled={isDownloading}
            >
              <Download className="h-4 w-4 mr-2" />
              {isDownloading ? 'Downloading...' : 'Download PDF'}
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Document Editor - Use DocumentDetailEditor for proper content rendering */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Document Content
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DocumentDetailEditor 
                  documentId={document.id}
                  onSave={async () => {
                    await loadDocument();
                  }}
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {isTeamOrEnterprise ? (
              <Tabs defaultValue="collaboration" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="collaboration">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Collaborate
                  </TabsTrigger>
                  <TabsTrigger value="sharing">
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="collaboration" className="mt-4">
                  <DocumentCollaboration documentId={document.id} />
                </TabsContent>
                
                <TabsContent value="sharing" className="mt-4">
                  <DocumentSharing 
                    documentId={document.id} 
                    organizationId={document.organization_id}
                  />
                </TabsContent>
              </Tabs>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Collaboration Features
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">
                    Upgrade to Team or Enterprise to unlock collaboration features:
                  </p>
                  <ul className="text-sm text-gray-600 space-y-2 mb-4">
                    <li>• Real-time collaboration</li>
                    <li>• Document sharing</li>
                    <li>• Comments and feedback</li>
                    <li>• Team workspaces</li>
                  </ul>
                  <Button 
                    onClick={() => navigate('/plans')}
                    className="w-full"
                  >
                    Upgrade Now
                  </Button>
                  {/* Debug info */}
                  {userSubscription && (
                    <div className="mt-4 p-2 bg-gray-100 rounded text-xs">
                      <p>Current Plan: {userSubscription.subscription_type} - {userSubscription.status}</p>
                      <p>Collaboration Access: {isTeamOrEnterprise ? 'Yes' : 'No'}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentDetail;
